import React, { useState } from 'react';
import axios from 'axios';
import { getCookies } from 'cookies-next';
import { config } from '../../data/config';
import { useToaster, Message } from 'rsuite';

interface AutoTranslateButtonProps {
    sourceText: string; // English text to translate
    targetLang: string; // Target language code (ku, fa, ar, tr)
    isHtml?: boolean; // Whether the text contains HTML
    onTranslated: (translation: string) => void; // Callback when translation is ready
    disabled?: boolean;
    className?: string;
}

const LANGUAGE_LABELS: Record<string, string> = {
    ku: 'Kurdish',
    fa: 'فارسی',
    ar: 'العربية',
    tr: 'Türkçe'
};

/**
 * Auto-translate button component
 * Translates English text to target language using OpenAI
 * Only shows when targetLang is not 'en' and sourceText exists
 */
export default function AutoTranslateButton({
    sourceText,
    targetLang,
    isHtml = false,
    onTranslated,
    disabled = false,
    className = ''
}: AutoTranslateButtonProps) {
    const [loading, setLoading] = useState(false);
    const cookie = getCookies();
    const toaster = useToaster();

    // Don't show button for English or when no source text
    if (targetLang === 'en' || !sourceText || !sourceText.trim()) {
        return null;
    }

    const handleTranslate = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${config.url}/v1/dashboard/translate`,
                {
                    text: sourceText,
                    targetLang,
                    isHtml
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + cookie['token']
                    }
                }
            );

            if (response.data.success && response.data.translation) {
                onTranslated(response.data.translation);
                toaster.push(
                    <Message showIcon type="success" closable>
                        <strong>ترجمه شد</strong>
                        <p>ترجمه به {LANGUAGE_LABELS[targetLang] || targetLang} انجام شد</p>
                    </Message>,
                    { placement: 'topEnd', duration: 2000 }
                );
            } else {
                throw new Error(response.data.message || 'Translation failed');
            }
        } catch (error: any) {
            console.error('Translation error:', error);
            toaster.push(
                <Message showIcon type="error" closable>
                    <strong>خطا در ترجمه</strong>
                    <p>{error.response?.data?.message || error.message || 'خطا در ترجمه متن'}</p>
                </Message>,
                { placement: 'topEnd', duration: 4000 }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleTranslate}
            disabled={disabled || loading}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                ${loading
                    ? 'bg-gray-100 text-gray-400 cursor-wait'
                    : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 shadow-sm hover:shadow-md'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
                ${className}`}
            title={`ترجمه خودکار از انگلیسی به ${LANGUAGE_LABELS[targetLang] || targetLang}`}
        >
            {loading ? (
                <>
                    <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>در حال ترجمه...</span>
                </>
            ) : (
                <>
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span>ترجمه خودکار</span>
                </>
            )}
        </button>
    );
}
